import React, { useState, useEffect } from 'react';
import { apiService, WhyChooseUs, WhyChooseUsBenefit, WhyChooseUsFeature, WhyChooseUsStat } from '../services/api';
import { Save, Award, Plus, Trash2, Edit2, CheckCircle, Smartphone, Shield, Zap } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getImageUrl } from '../utils/imageUtils';
import {
    FaTools, FaHotjar, FaWater, FaTv, FaSnowflake, FaSnowman,
    FaBolt, FaStar, FaHeart, FaCheckCircle, FaLightbulb, FaShieldAlt,
    FaSmile, FaThumbsUp, FaClock, FaLeaf, FaAward, FaUsers, FaCogs, FaRocket
} from "react-icons/fa";
import { IconType } from 'react-icons';

const ICON_MAP: Record<string, IconType> = {
    FaTools, FaHotjar, FaWater, FaTv, FaSnowflake, FaSnowman,
    FaBolt, FaStar, FaHeart, FaCheckCircle, FaLightbulb, FaShieldAlt,
    FaSmile, FaThumbsUp, FaClock, FaLeaf, FaAward, FaUsers, FaCogs, FaRocket
};

const ICON_OPTIONS = [
    { value: 'FaTools', label: 'Tools' },
    { value: 'FaHotjar', label: 'Hotjar (Flame)' },
    { value: 'FaWater', label: 'Water' },
    { value: 'FaTv', label: 'TV' },
    { value: 'FaSnowflake', label: 'Snowflake' },
    { value: 'FaSnowman', label: 'Snowman' },
    { value: 'FaBolt', label: 'Bolt (Energy)' },
    { value: 'FaStar', label: 'Star' },
    { value: 'FaHeart', label: 'Heart' },
    { value: 'FaCheckCircle', label: 'Check Circle' },
    { value: 'FaLightbulb', label: 'Lightbulb' },
    { value: 'FaShieldAlt', label: 'Shield' },
    { value: 'FaSmile', label: 'Smile' },
    { value: 'FaThumbsUp', label: 'Thumbs Up' },
    { value: 'FaClock', label: 'Clock' },
    { value: 'FaLeaf', label: 'Leaf' },
    { value: 'FaAward', label: 'Award' },
    { value: 'FaUsers', label: 'Users' },
    { value: 'FaCogs', label: 'Cogs (Settings)' },
    { value: 'FaRocket', label: 'Rocket' },
];

const WhyChooseUsPage: React.FC = () => {
    const { showToast } = useToast();
    const [selectedSection, setSelectedSection] = useState<WhyChooseUs | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [showSubItemForm, setShowSubItemForm] = useState<'benefit' | 'feature' | 'stat' | null>(null);
    const [editingSubItem, setEditingSubItem] = useState<WhyChooseUsBenefit | WhyChooseUsFeature | WhyChooseUsStat | null>(null);
    const [selectedIcon, setSelectedIcon] = useState<string>('FaTools');

    useEffect(() => {
        fetchSections();
    }, []);

    const openSubItemForm = (type: 'benefit' | 'feature' | 'stat', item?: any) => {
        setShowSubItemForm(type);
        setEditingSubItem(item || null);
        if (item && item.icon_class && ICON_MAP[item.icon_class]) {
            setSelectedIcon(item.icon_class);
        } else {
            setSelectedIcon('FaTools'); // Default
        }
    };

    // ... (fetchSections, handleSectionSubmit, handleDeleteSubItem same as before) 

    const fetchSections = async () => {
        try {
            setLoading(true);
            const data = await apiService.getWhyChooseUs();
            if (data && data.length > 0) {
                setSelectedSection(data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch Why Choose Us:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSectionSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSaving(true);
        const formData = new FormData(e.currentTarget);

        try {
            if (selectedSection?.id) {
                await apiService.updateWhyChooseUs(selectedSection.id, formData);
            } else {
                await apiService.createWhyChooseUs(formData);
            }
            showToast('Section updated successfully!', 'success');
            fetchSections();
        } catch (error) {
            console.error('Failed to save section:', error);
            showToast('Error saving section.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteSubItem = async (type: 'benefit' | 'feature' | 'stat', id: string) => {
        if (!window.confirm('Delete this item?')) return;
        try {
            if (type === 'benefit') await apiService.deleteWhyChooseUsBenefit(id);
            if (type === 'feature') await apiService.deleteWhyChooseUsFeature(id);
            if (type === 'stat') await apiService.deleteWhyChooseUsStat(id);
            showToast('Item deleted successfully!', 'success');
            fetchSections();
        } catch (error) {
            console.error(`Failed to delete ${type}:`, error);
            showToast(`Failed to delete ${type}.`, 'error');
        }
    };

    const handleSubItemSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        // ... (same as before) ...
        e.preventDefault();
        if (!selectedSection) return;

        const formData = new FormData(e.currentTarget);
        const payload: Record<string, any> = {
            why_choose_us: selectedSection.id,
            is_active: true
        };
        formData.forEach((value, key) => {
            payload[key] = value;
        });

        try {
            if (showSubItemForm === 'benefit') {
                if (editingSubItem) await apiService.updateWhyChooseUsBenefit(editingSubItem.id, payload);
                else await apiService.createWhyChooseUsBenefit(payload);
            } else if (showSubItemForm === 'feature') {
                if (editingSubItem) await apiService.updateWhyChooseUsFeature(editingSubItem.id, payload);
                else await apiService.createWhyChooseUsFeature(payload);
            } else if (showSubItemForm === 'stat') {
                if (editingSubItem) await apiService.updateWhyChooseUsStat(editingSubItem.id, payload);
                else await apiService.createWhyChooseUsStat(payload);
            }
            showToast('Item saved successfully!', 'success');
            setShowSubItemForm(null);
            setEditingSubItem(null);
            fetchSections();
        } catch (error) {
            console.error(`Failed to save ${showSubItemForm}:`, error);
            showToast(`Failed to save ${showSubItemForm}.`, 'error');
        }
    };

    // Helper helper to render icon
    const renderSelectedIcon = () => {
        const Icon = ICON_MAP[selectedIcon] as any;
        return Icon ? <Icon className="w-8 h-8 text-orange-500" /> : null;
    };

    // ... (rest of render until showSubItemForm) ...

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* ... (Header) ... */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Why Choose Us</h1>
                <p className="text-gray-600">Company highlights, feature lists, and statistics</p>
            </div>

            {/* ... (Main Grid) ... */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* ... (General Settings & Features) ... */}
                <div className="lg:col-span-2 space-y-6">
                    {/* ... (General Settings Form) ... */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        {/* ... */}
                        <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                            <Award className="h-5 w-5 mr-2 text-orange-600" /> General Settings
                        </h2>
                        <form onSubmit={handleSectionSubmit} className="space-y-4">
                            {/* ... same inputs ... */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Internal Name</label>
                                    <input name="section_name" defaultValue={selectedSection?.section_name} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Title</label>
                                    <input name="title" defaultValue={selectedSection?.title} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Badge/Tag</label>
                                    <input name="badge_text" defaultValue={selectedSection?.badge_text} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Subtitle</label>
                                    <input name="subtitle" defaultValue={selectedSection?.subtitle} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                    <textarea name="description" defaultValue={selectedSection?.description} rows={3} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                                <div>
                                    <select name="layout_type" defaultValue={selectedSection?.layout_type || 'features_with_stats'} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2">
                                        <option value="features_with_stats">Features with Stats</option>
                                        <option value="feature_grid">Feature Grid</option>
                                    </select>
                                </div>
                                <div className="flex items-center pt-6">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            defaultChecked={selectedSection?.is_active ?? true}
                                            value="true"
                                            className="w-5 h-5 text-orange-600 rounded border-gray-300 focus:ring-orange-500"
                                        />
                                        <span className="ml-2 text-sm font-medium text-gray-700">Is Active (Visible on Website)</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Image</label>
                                    {selectedSection?.center_image && (
                                        <div className="mt-2 mb-4 relative group w-full h-48 bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                                            <img
                                                src={getImageUrl(selectedSection.center_image) || undefined}
                                                alt="Current center"
                                                className="w-full h-full object-contain"
                                            />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <span className="text-white text-xs font-bold px-3 py-1 bg-black/50 rounded-full">Current Image</span>
                                            </div>
                                        </div>
                                    )}
                                    <input type="file" name="center_image" accept="image/*" className="mt-1 block w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-gray-100" />
                                </div>
                            </div>
                            <div className="flex justify-end pt-4">
                                <button type="submit" disabled={saving} className="inline-flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 transition font-bold">
                                    <Save className="h-4 w-4 mr-2" />
                                    {saving ? 'Saving...' : 'Save Settings'}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-lg font-bold flex items-center text-gray-900">
                                <Smartphone className="h-5 w-5 mr-2 text-blue-600" /> Features
                            </h2>
                            <button onClick={() => openSubItemForm('feature')} className="text-sm font-bold text-orange-600 flex items-center">
                                <Plus className="h-4 w-4 mr-1" /> Add Feature
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {selectedSection?.features?.map((feature) => (
                                <div key={feature.id} className="p-4 border border-gray-100 rounded-lg hover:border-orange-200 transition bg-gray-50/30 group">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <h3 className="font-bold text-gray-900 text-sm">{feature.title}</h3>
                                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{feature.description}</p>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition ml-2">
                                            <button onClick={() => openSubItemForm('feature', feature)} className="p-1 text-gray-400 hover:text-orange-600"><Edit2 className="h-4 w-4" /></button>
                                            <button onClick={() => handleDeleteSubItem('feature', feature.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        {/* Stats */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold flex items-center text-gray-900">
                                <Zap className="h-5 w-5 mr-2 text-yellow-500" /> Statistics
                            </h2>
                            <button onClick={() => openSubItemForm('stat')} className="text-xs font-bold text-orange-600 flex items-center">
                                <Plus className="h-3 w-3 mr-1" /> Add Stat
                            </button>
                        </div>
                        <div className="space-y-3">
                            {selectedSection?.statistics?.map((stat) => (
                                <div key={stat.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg group">
                                    <div>
                                        <div className="text-xl font-black text-gray-900">{stat.value}</div>
                                        <div className="text-xs text-gray-400 font-bold uppercase">{stat.label}</div>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                        <button onClick={() => openSubItemForm('stat', stat)} className="p-1 text-gray-400 hover:text-orange-600"><Edit2 className="h-3 w-3" /></button>
                                        <button onClick={() => handleDeleteSubItem('stat', stat.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                        {/* Benefits */}
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-lg font-bold flex items-center text-gray-900">
                                <Shield className="h-5 w-5 mr-2 text-green-600" /> Benefits
                            </h2>
                            <button onClick={() => openSubItemForm('benefit')} className="text-xs font-bold text-orange-600 flex items-center">
                                <Plus className="h-3 w-3 mr-1" /> Add Benefit
                            </button>
                        </div>
                        <div className="space-y-2">
                            {selectedSection?.benefits?.map((benefit) => (
                                <div key={benefit.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg group text-xs font-medium">
                                    <div className="flex items-center">
                                        <CheckCircle className="h-3 w-3 mr-2 text-green-500" />
                                        <span className="text-gray-700">{benefit.text}</span>
                                    </div>
                                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                                        <button onClick={() => openSubItemForm('benefit', benefit)} className="p-1 text-gray-400 hover:text-orange-600"><Edit2 className="h-3 w-3" /></button>
                                        <button onClick={() => handleDeleteSubItem('benefit', benefit.id)} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {showSubItemForm && (
                <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-gray-900 capitalize">
                                {editingSubItem ? 'Edit' : 'Add'} {showSubItemForm}
                            </h3>
                            <button onClick={() => setShowSubItemForm(null)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                        </div>
                        <form onSubmit={handleSubItemSubmit} className="p-6 space-y-4">
                            {showSubItemForm === 'benefit' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Benefit Text</label>
                                    <input name="text" defaultValue={(editingSubItem as WhyChooseUsBenefit)?.text} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                </div>
                            )}

                            {showSubItemForm === 'feature' && (
                                <>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Title</label>
                                        <input name="title" defaultValue={(editingSubItem as WhyChooseUsFeature)?.title} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Description</label>
                                        <textarea name="description" defaultValue={(editingSubItem as WhyChooseUsFeature)?.description} rows={3} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Icon Class</label>
                                            <div className="flex gap-2 items-center">
                                                <select
                                                    name="icon_class"
                                                    defaultValue={(editingSubItem as WhyChooseUsFeature)?.icon_class || 'FaTools'}
                                                    onChange={(e) => setSelectedIcon(e.target.value)}
                                                    className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2"
                                                >
                                                    {ICON_OPTIONS.map(opt => (
                                                        <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                                                    ))}
                                                </select>
                                                <div className="mt-1 p-2 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                                    {renderSelectedIcon()}
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Color</label>
                                            <input name="icon_color" type="color" defaultValue={(editingSubItem as WhyChooseUsFeature)?.icon_color || '#ea580c'} className="mt-1 h-10 w-full border border-gray-200 rounded-lg p-1" />
                                        </div>
                                    </div>
                                </>
                            )}

                            {showSubItemForm === 'stat' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <label className="block text-sm font-medium text-gray-700">Label</label>
                                        <input name="label" defaultValue={(editingSubItem as WhyChooseUsStat)?.label} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Value</label>
                                        <input name="value" defaultValue={(editingSubItem as WhyChooseUsStat)?.value} required className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Icon Class</label>
                                        <div className="flex gap-2 items-center">
                                            <select
                                                name="icon_class"
                                                defaultValue={(editingSubItem as WhyChooseUsStat)?.icon_class || 'FaStar'}
                                                onChange={(e) => setSelectedIcon(e.target.value)}
                                                className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2"
                                            >
                                                {ICON_OPTIONS.map(opt => (
                                                    <option key={opt.value} value={opt.value}>{opt.label} ({opt.value})</option>
                                                ))}
                                            </select>
                                            <div className="mt-1 p-2 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                                                {renderSelectedIcon()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Display Order</label>
                                <input name="order" type="number" defaultValue={editingSubItem?.order || 0} className="mt-1 block w-full border border-gray-200 rounded-lg px-4 py-2" />
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-gray-50">
                                <button type="button" onClick={() => setShowSubItemForm(null)} className="px-4 py-2 text-gray-600 font-medium">Cancel</button>
                                <button type="submit" className="px-8 py-2 bg-orange-600 text-white rounded-lg font-bold hover:bg-orange-700 transition">
                                    {editingSubItem ? 'Update' : 'Add'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WhyChooseUsPage;
